import { ArrowDownLeft, ArrowUpRight } from 'lucide-react';
import type React from 'react';
import type { LedgerEntryDto } from '@/api/reconciliation';
import { Checkbox } from '@/components/ui/checkbox';
import { formatExactAmount } from '@/lib/format-exact-amount';
import { cn, formatCalendarDate } from '@/lib/utils';

/**
 * One platform ledger row.
 *
 * The row shows the `flow` label, Receipt or Disbursement, because the operator's
 * own workbook has no credit/debit column. Both label pairs are carried on the
 * data and neither is dropped; `direction` mirrors the bank's view and is exposed
 * as a tooltip rather than as a second visible column.
 *
 * As in the bank pane, the checkbox is inert: selection belongs to the matching
 * story and is rendered disabled so the layout is the one that story builds on.
 */
export const LedgerEntryRow: React.FC<{ entry: LedgerEntryDto }> = ({ entry }) => {
  const isReceipt = entry.flow === 'Receipt';

  return (
    <div
      data-matched={entry.matched ? 'true' : undefined}
      className="flex items-center gap-3 border-b border-border px-4 py-2.5 last:border-b-0"
    >
      {/* A matched row is labelled and muted rather than faded: text faded far enough
          to read as done falls below 4.5:1, and fading alone says nothing to a screen
          reader. The bank pane's rows follow the same rule. */}
      <Checkbox
        checked={false}
        disabled
        aria-label={`Select ${entry.clientName} ${entry.paymentId}`}
        className="shrink-0 rounded-[4px] border-border"
      />

      <span className="shrink-0" title={entry.direction}>
        {isReceipt ? (
          <ArrowDownLeft className="h-3.5 w-3.5 text-credit" aria-hidden="true" />
        ) : (
          <ArrowUpRight className="h-3.5 w-3.5 text-debit" aria-hidden="true" />
        )}
        <span className="sr-only">{`${entry.flow}, ${entry.direction}`}</span>
      </span>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span
            className={cn(
              'truncate font-medium text-[13px]',
              entry.matched ? 'text-muted-foreground' : 'text-foreground'
            )}
          >
            {entry.clientName}
          </span>
          <span
            className={cn(
              'shrink-0 rounded px-1.5 py-0.5 font-medium text-[10px]',
              isReceipt ? 'bg-credit/10 text-credit' : 'bg-debit/10 text-debit'
            )}
          >
            {entry.category}
          </span>
          {entry.matched && (
            <span className="shrink-0 rounded bg-matched/10 px-1.5 py-0.5 font-medium text-[10px] text-matched">
              Matched
            </span>
          )}
        </div>
        <span className="mt-0.5 block truncate font-mono text-[10px] text-muted-foreground">
          {entry.clientNumber} &middot; {entry.paymentMethod} &middot; {entry.paymentId} &middot;{' '}
          {entry.paymentFileReference}
        </span>
      </div>

      <div className="shrink-0 text-right">
        <span
          className={cn(
            'font-mono font-medium text-[13px] tabular-nums',
            isReceipt ? 'text-credit' : 'text-debit',
            entry.matched && 'text-muted-foreground'
          )}
        >
          {isReceipt ? '+' : '-'}${formatExactAmount(entry.amount)}
        </span>
        <span className="block font-mono text-[10px] text-muted-foreground">
          {entry.currency} &middot; {formatCalendarDate(entry.date)}
        </span>
      </div>
    </div>
  );
};
